import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Signin.css';

interface SigninProps {
  onNavigate?: (page: 'home' | 'archive' | 'band-tees' | 'about' | 'signup' | 'signin' | 'account' | 'admin' | 'checkout') => void;
}

const Signin: React.FC<SigninProps> = ({ onNavigate }) => {
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        console.log('Attempting sign in with:', formData.email);
        const { data, error } = await signIn(formData.email, formData.password);
        console.log('Sign in result:', { data, error });

        if (error) {
          console.error('Sign in error:', error);
          setErrors({ general: (error as any)?.message || 'Failed to sign in' });
          return;
        }

        // Success - wait for auth state to update, then navigate
        console.log('Sign in successful, waiting for auth state update...');

        // Wait for auth state to update before navigating
        let attempts = 0;
        const checkAuthState = () => {
          attempts++;
          console.log(`Checking auth state, attempt ${attempts}`);

          // Import useAuth to check current auth state
          // For now, just navigate after a delay
          setTimeout(() => {
            setFormData({
              email: '',
              password: '',
              remember: false
            });

            if (onNavigate) {
              onNavigate('account');
            }
          }, 1500);
        };

        checkAuthState();

      } catch (error) {
        console.error('Unexpected error during sign in:', error);
        setErrors({ general: 'An unexpected error occurred' });
      }
    }
  };

  const handleSignupClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate('signup');
    }
  };

  return (
    <div className="signin">
      <div className="signin-container">
        <h1>Welcome Back</h1>
        <p>Sign in to your ANTI-STYLE HYPOTHESIS account</p>

        <form onSubmit={handleSubmit} className="signin-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email"
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
            />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>

          <div className="form-options">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="remember"
                checked={formData.remember}
                onChange={handleInputChange}
              />
              <span className="checkmark"></span>
              Remember me
            </label>
            <a href="#forgot" className="forgot-link">Forgot password?</a>
          </div>

          <button type="submit" className="signin-btn">
            Sign In
          </button>
        </form>

        <div className="signup-link">
          <p>Don't have an account? <a href="#" onClick={handleSignupClick}>Create one here</a></p>
        </div>

        {/* Social sign-in options removed — only email/password allowed */}
      </div>
    </div>
  );
};

export default Signin;
